
import { useState, useEffect } from "react";
import { useSession } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";

export function useAdminView() {
  const session = useSession();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminView, setIsAdminView] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check admin view status from session storage on mount and when storage changes
  useEffect(() => {
    const checkAdminView = () => {
      // Only check admin view if user is logged in
      if (!session?.user) {
        setIsAdminView(false);
        sessionStorage.removeItem('adminViewEnabled');
        return;
      }
      
      const adminViewEnabled = sessionStorage.getItem('adminViewEnabled') === 'true';
      setIsAdminView(adminViewEnabled);
      console.log("Navigation: Admin view is", adminViewEnabled ? "enabled" : "disabled");
    };
    
    // Check on mount
    checkAdminView();
    
    // Add event listener for storage changes
    window.addEventListener('storage', checkAdminView);
    
    return () => {
      window.removeEventListener('storage', checkAdminView);
    };
  }, [session]);

  // Check admin status whenever session changes
  useEffect(() => {
    async function checkAdminStatus() {
      if (!session?.user?.id) {
        setIsAdmin(false);
        setIsAdminView(false);
        sessionStorage.removeItem('adminViewEnabled');
        setLoading(false);
        return;
      }

      try {
        // Check admin status in the database
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .eq('role', 'admin')
          .maybeSingle();
        
        if (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        } else if (data) {
          console.log('User is an admin:', session.user.id);
          setIsAdmin(true);
          
          // Check if admin view state is set in sessionStorage
          const adminViewState = sessionStorage.getItem('adminViewEnabled');
          if (adminViewState === null) {
            // Default to admin view for admin users if not set
            sessionStorage.setItem('adminViewEnabled', 'true');
            setIsAdminView(true);
            
            // Dispatch a storage event so other components update
            window.dispatchEvent(new Event('storage'));
          } else {
            setIsAdminView(adminViewState === 'true');
          }
        } else {
          console.log('User is not an admin:', session.user.id);
          setIsAdmin(false);
          setIsAdminView(false);
          sessionStorage.removeItem('adminViewEnabled');
        }
        
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    }

    checkAdminStatus();
  }, [session]);

  return { isAdmin, isAdminView, loading };
}
